import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import VehicleInspectionUpload from './VehicleInspectionUpload';
import apiClient from '../../../pages/keycloak/axiosInstance';

const VehicleInspection = () => {
  const [vehicleDetails, setVehicleDetails] = useState({ orderNumber: '', vehicleNumber: '', vehicleType: '' });
  const [quantityDetails, setQuantityDetails] = useState({ noOfPallets: '', totalQuantity: '', shippedQuantity: '' });
  const [receivingDetails, setReceivingDetails] = useState({ location: '', receivedBy: '', receivedDateTime: '', remarks: '', checked: false });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [newPallet, setNewPallet] = useState({ pallet: '', quantity: '' });
  const [submissionProgress, setSubmissionProgress] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const CHUNK_SIZE = 5 * 1024 * 1024; 
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
    setVehicleDetails({
      orderNumber: inspectionData.orderNo || '',
      vehicleNumber: inspectionData.vehicleNo || '',
      vehicleType: inspectionData.vehicleType || '',
    });
    setQuantityDetails({
      noOfPallets: inspectionData.noOfPallets || '',
      totalQuantity: '',
      shippedQuantity: '',
    });

    const fetchPalletDetails = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');

        const vehicleTypeLower = (inspectionData.vehicleType || '').toLowerCase().trim();

        const payload = {
          projectId,
          dateOfShipping: inspectionData.dateOfShipping || '',
          truckNumber: vehicleTypeLower === 'truck' ? inspectionData.vehicleNo || '' : '',
          containerNumber: vehicleTypeLower === 'container' ? inspectionData.vehicleNo || '' : '',
        };

        if (vehicleTypeLower !== 'truck' && vehicleTypeLower !== 'container') {
          console.warn(`Unknown vehicle type "${inspectionData.vehicleType}". Sending empty truckNumber and containerNumber.`);
          payload.truckNumber = '';
          payload.containerNumber = '';
        }

        const response = await apiClient.post(`get_pallet_details`, payload);

        if (response.data.status === 'success') {
          setPallets(response.data.data.pallets.map((p, i) => ({
            id: i + 1,
            pallet: p.pallet_number || `P${String(i + 1).padStart(3, '0')}`,
            quantity: p.unique_module_count || 0,
            action: 'Accept',
            flag: false,
            photos: [],
            remarks: '',
            isManual: false,
          })));
          setQuantityDetails((prev) => ({ ...prev, 
            totalQuantity: response.data.data.total_qty || '',
            shippedQuantity: response.data.data.shipped_qty_claimed_by_supplier || '',
          }));
        }
      } catch (error) {
        console.error('Error fetching pallet details:', error);
      }
    };
    fetchPalletDetails();
  }, []);

  const handleChange = (setter) => (e) => {
    const { name, value, type, checked } = e.target;
    setter((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handlePalletChange = (id, field, value) => setPallets((prev) =>
    prev.map((p) => p.id === id ? { ...p, [field]: value, flag: field === 'action' && value !== 'Accept' ? true : p.action !== 'Accept' } : p)
  );

  const handleFileUpload = (id, files) => {
    setPallets((prev) => prev.map((p) => p.id === id ? {
      ...p,
      photos: [...p.photos, ...Array.from(files).map(file => ({
        id: uuidv4(),
        file,
        name: file.name,
      }))],
    } : p));
  };

  const removePhoto = (palletId, photoId) => {
    setPallets((prev) =>
      prev.map((p) =>
        p.id === palletId
          ? { ...p, photos: p.photos.filter((ph) => ph.id !== photoId) }
          : p
      )
    );
  };

  const addNewPallet = () => {
    if (newPallet.pallet && newPallet.quantity) {
      setPallets((prev) => [...prev, {
        id: prev.length + 1,
        pallet: newPallet.pallet,
        quantity: parseInt(newPallet.quantity),
        action: 'Accept',
        flag: false,
        photos: [],
        remarks: '',
        isManual: true,
      }]);
      setNewPallet({ pallet: '', quantity: '' });
    }
  };

  const deletePallet = (id) => setPallets((prev) => prev.filter((p) => p.id !== id));

  const totalAcceptedQuantity = pallets.filter((p) => p.action === 'Accept').reduce((sum, p) => sum + p.quantity, 0);

  const handleChunkedFileUpload = async (file, metadata, fileId) => {
    const { fileType, fileIndex, pallet, docType } = metadata;
    const projectId = localStorage.getItem('selectedProject') || '';
    const supplierName = sessionStorage.getItem('selectedSupplierTab') || '';
    const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
    const fileName = file.name.replace(/\s+/g, '_');
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

    for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
      const start = chunkIndex * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const chunkSize = chunk.size;

      const formData = new FormData();
      formData.append('isChunked', 'true');
      formData.append('fileType', fileType);
      formData.append('fileIndex', fileIndex);
      formData.append('fileId', fileId);
      formData.append('fileName', fileName);
      formData.append('chunkIndex', chunkIndex);
      formData.append('totalChunks', totalChunks);
      formData.append('chunk', chunk);
      formData.append('chunkSize', chunkSize.toString());
      formData.append('projectId', projectId);
      formData.append('supplierName', supplierName);
      formData.append('vehicleDetails[vehicleNumber]', inspectionData.vehicleNo || '');
      formData.append('vehicleDetails[vehicleType]', inspectionData.vehicleType || '');
      formData.append('vehicleDetails[dateOfShipping]', inspectionData.dateOfShipping || '');
      if (fileType === 'photo' && pallet) {
        formData.append('pallet', pallet);
      }
      if (fileType === 'document' && docType) {
        formData.append('docType', docType);
      }

      try {
        const response = await apiClient.post(
          `create_vehicle_inspection`,
          formData,
          {
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: (progressEvent) => {
              const chunkProgress = (chunkIndex / totalChunks) * 100;
              const chunkSizeProgress = (progressEvent.loaded / progressEvent.total) * (100 / totalChunks);
              const totalProgress = Math.min(chunkProgress + chunkSizeProgress, 100);
              setSubmissionProgress((prev) => ({
                ...prev,
                [fileId]: totalProgress,
              }));
            },
          }
        );
        console.log(`Chunk ${chunkIndex + 1}/${totalChunks} uploaded for ${fileName}:`, response.data);
      } catch (error) {
        console.error(
          `Error uploading chunk ${chunkIndex + 1}/${totalChunks} for ${fileName}:`,
          error.response?.data || error.message
        );
        throw error; // Stop on error to prevent proceeding to the next chunk
      }
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmissionProgress({});

    const projectId = localStorage.getItem('selectedProject') || '';
    const supplierName = sessionStorage.getItem('selectedSupplierTab') || '';
    const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');

    if (!projectId) {
      alert('Project ID is missing. Please select a project.');
      setIsSubmitting(false);
      return;
    }

    const formData = new FormData();
    formData.append('projectId', projectId);
    formData.append('supplierName', supplierName);
    formData.append('vehicleDetails[orderNumber]', vehicleDetails.orderNumber || '');
    formData.append('vehicleDetails[vehicleNumber]', vehicleDetails.vehicleNumber || '');
    formData.append('vehicleDetails[vehicleType]', vehicleDetails.vehicleType || '');
    formData.append('vehicleDetails[numberOfPallets]', quantityDetails.noOfPallets || '');
    formData.append('vehicleDetails[dateOfShipping]', inspectionData.dateOfShipping || '');
    formData.append('vehicleDetails[moduleType]', inspectionData.moduleType || '');
    formData.append('vehicleDetails[packingList]', inspectionData.packingList || '');
    formData.append('quantityDetails[noOfPallets]', quantityDetails.noOfPallets || '');
    formData.append('quantityDetails[totalQuantity]', quantityDetails.totalQuantity || '');
    formData.append('quantityDetails[shippedQuantity]', quantityDetails.shippedQuantity || '');
    formData.append('receivingDetails[location]', receivingDetails.location || '');
    formData.append('receivingDetails[receivedBy]', receivingDetails.receivedBy || '');
    formData.append('receivingDetails[receivedDateTime]', receivingDetails.receivedDateTime || '');
    formData.append('receivingDetails[remarks]', receivingDetails.remarks || '');
    formData.append('receivingDetails[checked]', receivingDetails.checked.toString());
    formData.append('totalAcceptedQuantity', totalAcceptedQuantity.toString());

    pallets.forEach((p, index) => {
      formData.append(`palletDetails[${index}][pallet]`, p.pallet || '');
      formData.append(`palletDetails[${index}][quantity]`, p.quantity.toString());
      formData.append(`palletDetails[${index}][action]`, p.action || 'Accept');
      formData.append(`palletDetails[${index}][flag]`, p.flag.toString());
      formData.append(`palletDetails[${index}][remarks]`, p.remarks || '');
    });

    // Log FormData for debugging
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }

    const photoUploads = pallets.flatMap((pallet, palletIndex) =>
      pallet.photos.map((photo, photoIndex) => ({
        file: photo.file,
        fileId: photo.id,
        metadata: {
          fileType: 'photo',
          fileIndex: photoIndex,
          pallet: pallet.pallet,
        },
      }))
    );

    const documentUploads = uploadedFiles.flatMap((doc, docIndex) =>
      doc.files.map((file, fileIndex) => ({
        file: file.file,
        fileId: file.id,
        metadata: {
          fileType: 'document',
          fileIndex: fileIndex,
          docType: doc.type,
        },
      }))
    );

    const allUploads = [...photoUploads, ...documentUploads];

    try {
      // Upload chunks one by one across all files
      for (const upload of allUploads) {
        const { file, fileId, metadata } = upload;
        await handleChunkedFileUpload(file, metadata, fileId);
        console.log(`Completed upload for file: ${file.name}`);
      }

      // Submit the final form data after all chunks are uploaded
      const response = await apiClient.post(
        `create_vehicle_inspection`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      );
      console.log('API Response:', response.data);
      navigate('/receiving-modules');
      sessionStorage.removeItem('inspectionData');
    } catch (error) {
      alert(`Failed to submit vehicle inspection: ${error.response?.data?.message || error.message}`);
    } finally {
      setIsSubmitting(false);
      setSubmissionProgress({});
    }
  };

  const handleBackToForms = () => {
    navigate('/receiving-modules');
    sessionStorage.removeItem('inspectionData');
  };

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <button
        onClick={handleBackToForms}
        className="mb-4 inline-flex items-center text-gray-700 hover:text-gray-600 font-bold text-md focus:outline-none focus:underline"
      >
        ← Back to Vehicles Awaiting List
      </button>
      <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-lg p-8">
        <h1 className="text-3xl text-center font-bold text-gray-800 mb-6">Vehicle Inspection</h1>
        <form onSubmit={handleSubmit}>
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Enter Vehicle Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {['orderNumber', 'vehicleNumber', 'vehicleType'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-600">{field.replace(/([A-Z])/g, ' $1').trim()}</label>
                  <input
                    type="text"
                    name={field}
                    readOnly
                    value={vehicleDetails[field]}
                    onChange={handleChange(setVehicleDetails)}
                    className="mt-1 w-full bg-gray-100 p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                    disabled={isSubmitting}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Quantity and Inspection Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['noOfPallets', 'totalQuantity', 'shippedQuantity'].map((field) => (
                <div key={field}>
                  <label className="block text-sm font-medium text-gray-600">{field === 'noOfPallets' ? 'No. Of Pallets' :field === 'totalQuantity' ? 'Total Quantity' : field === 'shippedQuantity' ? 'Shipped Quantity' : ''}</label>
                  <input
                    type="number"
                    name={field}
                    readOnly
                    value={quantityDetails[field]}
                    onChange={handleChange(setQuantityDetails)}
                    className="mt-1 w-full p-3 bg-gray-100 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                    placeholder={`Enter ${field === 'noOfPallets' ? 'No. Of Pallets' :field === 'totalQuantity' ? 'Total Quantity' : field === 'shippedQuantity' ? 'Shipped Quantity' : ''}`}
                    disabled={isSubmitting}
                  />
                </div>
              ))}
            </div>
          </div>
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Pallet Level Check</h2>
            <div className="overflow-x-auto rounded-lg shadow-sm">
              <table className="min-w-full bg-white border border-gray-200">
                <thead>
                  <tr className="bg-gray-700 text-white">
                    {['Pallets', 'Pallet Quantity', 'Actions', 'Flag', 'Photos Upload', 'Remarks', ''].map((header) => (
                      <th key={header} className="p-4 text-left text-sm font-semibold">{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {pallets.map((pallet) => (
                    <tr key={pallet.id} className="border-t hover:bg-gray-50">
                      <td className="p-4 text-gray-700">{pallet.pallet}</td>
                      <td className="p-4 text-gray-700">{pallet.quantity}</td>
                      <td className="p-4">
                        <select
                          value={pallet.action}
                          onChange={(e) => handlePalletChange(pallet.id, 'action', e.target.value)}
                          className="p-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
                          disabled={isSubmitting}
                        >
                          {['Accept', 'Damaged', 'Not Received', 'Short', 'Different Make'].map((option) => (
                            <option key={option} value={option}>{option}</option>
                          ))}
                        </select>
                      </td>
                      <td className="p-4">
                        <input
                          type="checkbox"
                          checked={pallet.flag}
                          disabled={pallet.action === 'Accept' || isSubmitting}
                          onChange={(e) => handlePalletChange(pallet.id, 'flag', e.target.checked)}
                          className="h-4 w-4 text-gray-600 rounded focus:ring-gray-500"
                        />
                      </td>
                      <td className="p-4">
                        <input
                          type="file"
                          multiple
                          onChange={(e) => handleFileUpload(pallet.id, e.target.files)}
                          className="text-sm text-gray-600"
                          disabled={isSubmitting}
                        />
                        {pallet.photos.length > 0 && (
                          <div className="mt-2 space-y-2">
                            {pallet.photos.map((photo) => (
                              <div
                                key={photo.id}
                                className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded-md"
                              >
                                <div className="flex-1">
                                  <p className="text-xs text-gray-600 truncate max-w-xs">
                                    {photo.name}
                                  </p>
                                  {submissionProgress[photo.id] !== undefined && (
                                    <div className="mt-1">
                                      <progress
                                        value={submissionProgress[photo.id]}
                                        max="100"
                                        className="w-full h-2 rounded-full"
                                      />
                                      <p className="text-xs text-gray-600 mt-1">
                                        {Math.round(submissionProgress[photo.id])}%
                                      </p>
                                    </div>
                                  )}
                                </div>
                                {!isSubmitting && (
                                  <button
                                    type="button"
                                    onClick={() => removePhoto(pallet.id, photo.id)}
                                    className="ml-4 text-red-600 hover:text-red-800 flex-shrink-0"
                                    title="Remove file"
                                    // Optional: disable remove if this file is currently uploading
                                    disabled={submissionProgress[photo.id] !== undefined}
                                  >
                                    <svg
                                      className="w-5 h-5"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="p-1">
                        <textarea
                          value={pallet.remarks}
                          onChange={(e) => handlePalletChange(pallet.id, 'remarks', e.target.value)}
                          className="w-full p-2 h-10 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder="Enter remarks"
                          disabled={isSubmitting}
                        />
                      </td>
                      <td className="p-4">
                        {pallet.isManual && (
                          <button
                            type="button"
                            onClick={() => deletePallet(pallet.id)}
                            className="text-red-600 hover:text-red-800"
                            disabled={isSubmitting}
                          >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t bg-gray-50">
                    {['pallet', 'quantity'].map((field) => (
                      <td key={field} className="p-4">
                        <input
                          type={field === 'quantity' ? 'number' : 'text'}
                          name={field}
                          value={newPallet[field]}
                          onChange={handleChange(setNewPallet)}
                          onBlur={addNewPallet}
                          className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                          placeholder={`Enter ${field} ${field === 'quantity' ? '' : 'ID'}`}
                          disabled={isSubmitting}
                        />
                      </td>
                    ))}
                    <td colSpan="5" className="p-4"></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="mt-4 text-lg font-semibold text-gray-700">
              Total Accepted Quantity: <span className="text-green-600">{totalAcceptedQuantity}</span>
            </p>
          </div>
          <div className="mb-8">
            <h2 className="text-xl font-semibold text-gray-700 mb-4">Receiving & Verification Details</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {['location', 'receivedBy', 'receivedDateTime', 'remarks'].map((field) => (
              <div key={field}>
                <label className="block text-sm font-medium text-gray-600">
                  {field.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                <input
                  type={field === 'receivedDateTime' ? 'datetime-local' : 'text'}
                  name={field}
                  required
                  value={receivingDetails[field]}
                  onChange={handleChange(setReceivingDetails)}
                  className="mt-1 w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
                  placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                  disabled={isSubmitting}
                  {...(field === "receivedDateTime" && {
                    max: new Date().toISOString().split("T")[0] + "T23:59",
                  })}
                />
              </div>
              ))}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="checked"
                  checked={receivingDetails.checked}
                  onChange={handleChange(setReceivingDetails)}
                  className="h-4 w-4 rounded accent-green-500"
                  disabled={isSubmitting}
                  required
                />
                <label className="ml-2 text-sm font-medium text-gray-600">Checked</label>
              </div>
            </div>
          </div>
          <VehicleInspectionUpload onFilesChange={setUploadedFiles} submissionProgress={submissionProgress} />
          <div className="flex justify-end">
            <button
              type="submit"
              className={`bg-gray-600 text-white px-6 py-3 rounded-lg shadow-md ${isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-700'}`}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </div>
        </form>
      </div>
      <style>
        {`
          progress::-webkit-progress-bar {
            background-color: #e5e7eb;
            border-radius: 9999px;
          }
          progress::-webkit-progress-value {
            background-color: #10b981;
            border-radius: 9999px;
          }
          progress::-moz-progress-bar {
            background-color: #10b981;
            border-radius: 9999px;
          }
        `}
      </style>
    </div>
  );
};

export default VehicleInspection;

// import React, { useState, useEffect } from 'react';
// import { useNavigate } from 'react-router-dom';
// import axios from 'axios';
// import VehicleInspectionUpload from './VehicleInspectionUpload';

// const VehicleInspection = () => {
//   const [vehicleDetails, setVehicleDetails] = useState({ orderNumber: '', vehicleNumber: '', vehicleType: '' });
//   const [quantityDetails, setQuantityDetails] = useState({ noOfPallets: '', totalQuantity: '' });
//   const [receivingDetails, setReceivingDetails] = useState({ location: '', receivedBy: '', receivedDateTime: '', remarks: '', checked: false });
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [pallets, setPallets] = useState([]);
//   const [newPallet, setNewPallet] = useState({ pallet: '', quantity: '' });
//   const navigate = useNavigate();

//   useEffect(() => {
//     const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
//     setVehicleDetails({
//       orderNumber: inspectionData.orderNo || '',
//       vehicleNumber: inspectionData.vehicleNo || '',
//       vehicleType: inspectionData.vehicleType || '',
//     });
//     setQuantityDetails({
//       noOfPallets: inspectionData.noOfPallets || '',
//       totalQuantity: '',
//     });

//     const fetchPalletDetails = async () => {
//       try {
//         const projectId = localStorage.getItem('selectedProject');
//         const response = await axios.post('https://redoms-backend-dev.m-devsecops.com/get_pallet_details', {
//           projectId,
//           truckNumber: inspectionData.vehicleNo || '',
//           containerNumber: '',
//           dateOfShipping: inspectionData.dateOfShipping || '',
//         });
//         if (response.data.status === 'success') {
//           setPallets(response.data.data.pallets.map((p, i) => ({
//             id: i + 1,
//             pallet: p.pallet_number || `P${String(i + 1).padStart(3, '0')}`,
//             quantity: p.unique_module_count || 0,
//             action: 'Accept',
//             flag: false,
//             photos: [],
//             remarks: '',
//             isManual: false,
//           })));
//           setQuantityDetails((prev) => ({ ...prev, totalQuantity: response.data.data.total_qty || '' }));
//         }
//       } catch (error) {
//         console.error('Error fetching pallet details:', error);
//       }
//     };
//     fetchPalletDetails();
//   }, []);

//   const fileToBase64 = (file) => new Promise((resolve, reject) => {
//     const reader = new FileReader();
//     reader.readAsDataURL(file);
//     reader.onload = () => resolve(reader.result);
//     reader.onerror = reject;
//   });

//   const handleChange = (setter) => (e) => {
//     const { name, value, type, checked } = e.target;
//     setter((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
//   };

//   const handlePalletChange = (id, field, value) => setPallets((prev) =>
//     prev.map((p) => p.id === id ? { ...p, [field]: value, flag: field === 'action' && value !== 'Accept' ? true : p.action !== 'Accept' } : p)
//   );

//   const handleFileUpload = async (id, files) => {
//     try {
//       const base64Files = await Promise.all(Array.from(files).map(fileToBase64));
//       setPallets((prev) => prev.map((p) => p.id === id ? { ...p, photos: base64Files.length > 0 ? base64Files : [] } : p));
//     } catch (error) {
//       console.error('Error converting files to Base64:', error);
//     }
//   };

//   const addNewPallet = () => {
//     if (newPallet.pallet && newPallet.quantity) {
//       setPallets((prev) => [...prev, {
//         id: prev.length + 1,
//         pallet: newPallet.pallet,
//         quantity: parseInt(newPallet.quantity),
//         action: 'Accept',
//         flag: false,
//         photos: [],
//         remarks: '',
//         isManual: true,
//       }]);
//       setNewPallet({ pallet: '', quantity: '' });
//     }
//   };

//   const deletePallet = (id) => setPallets((prev) => prev.filter((p) => p.id !== id));

//   const totalAcceptedQuantity = pallets.filter((p) => p.action === 'Accept').reduce((sum, p) => sum + p.quantity, 0);

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     const inspectionData = JSON.parse(sessionStorage.getItem('inspectionData') || '{}');
//     const formData = {
//       projectId: localStorage.getItem('selectedProject') || '',
//       supplierName: sessionStorage.getItem('selectedSupplierTab') || '', 
//       vehicleDetails: {
//         ...vehicleDetails,
//         numberOfPallets: quantityDetails.noOfPallets,
//         dateOfShipping: inspectionData.dateOfShipping || '',
//         moduleType: inspectionData.moduleType || '',
//         packingList: inspectionData.packingList || '',
//       },
//       quantityDetails,
//       receivingDetails,
//       palletDetails: pallets.map(({ id, isManual, ...rest }) => rest),
//       documentUploads: uploadedFiles.map(({ type, files, uploadedAt }) => ({
//         type,
//         files: files.map((f) => ({ name: f.name, base64: f.base64 })),
//         uploadedAt,
//       })),
//       totalAcceptedQuantity,
//     };
//     try {
//       const response = await axios.post('https://redoms-backend-dev.m-devsecops.com/create_vehicle_inspection', formData);
//       // console.log('API Response:', response.data);
//       navigate('/receiving-modules');
//       sessionStorage.removeItem('inspectionData');
//     } catch (error) {
//       console.error('Error submitting vehicle inspection:', error);
//     }
//   };

//   const handleBackToForms = () => {
//     navigate('/receiving-modules');
//     sessionStorage.removeItem('inspectionData');
//   };

//   return (
//     <div className="min-h-screen bg-gray-100 p-6">
//       <button
//         onClick={handleBackToForms}
//         className="mb-4 inline-flex items-center text-gray-700 hover:text-gray-600 font-bold text-md focus:outline-none focus:underline"
//       >
//         ← Back to Vehicles Awaiting List
//       </button>
//       <div className="max-w-7xl mx-auto bg-white shadow-xl rounded-lg p-8">
//         <h1 className="text-3xl text-center font-bold text-gray-800 mb-6">Vehicle Inspection</h1>
//         <form onSubmit={handleSubmit}>
//           <div className="mb-8">
//             <h2 className="text-xl font-semibold text-gray-700 mb-4">Enter Vehicle Details</h2>
//             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//               {['orderNumber', 'vehicleNumber', 'vehicleType'].map((field) => (
//                 <div key={field}>
//                   <label className="block text-sm font-medium text-gray-600">{field.replace(/([A-Z])/g, ' $1').trim()}</label>
//                   <input
//                     type="text"
//                     name={field}
//                     value={vehicleDetails[field]}
//                     onChange={handleChange(setVehicleDetails)}
//                     className="mt-1 w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
//                     placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
//                   />
//                 </div>
//               ))}
//             </div>
//           </div>
//           <div className="mb-8">
//             <h2 className="text-xl font-semibold text-gray-700 mb-4">Quantity and Inspection Details</h2>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {['noOfPallets', 'totalQuantity'].map((field) => (
//                 <div key={field}>
//                   <label className="block text-sm font-medium text-gray-600">{field === 'noOfPallets' ? 'No. Of Pallets' : 'Total Quantity'}</label>
//                   <input
//                     type="number"
//                     name={field}
//                     value={quantityDetails[field]}
//                     onChange={handleChange(setQuantityDetails)}
//                     className="mt-1 w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
//                     placeholder={`Enter ${field === 'noOfPallets' ? 'number of pallets' : 'total quantity'}`}
//                   />
//                 </div>
//               ))}
//             </div>
//           </div>
//           <div className="mb-8">
//             <h2 className="text-xl font-semibold text-gray-700 mb-4">Pallet Level Check</h2>
//             <div className="overflow-x-auto rounded-lg shadow-sm">
//               <table className="min-w-full bg-white border border-gray-200">
//                 <thead>
//                   <tr className="bg-gray-700 text-white">
//                     {['Pallets', 'Pallet Quantity', 'Actions', 'Flag', 'Photos Upload', 'Remarks', ''].map((header) => (
//                       <th key={header} className="p-4 text-left text-sm font-semibold">{header}</th>
//                     ))}
//                   </tr>
//                 </thead>
//                 <tbody>
//                   {pallets.map((pallet) => (
//                     <tr key={pallet.id} className="border-t hover:bg-gray-50">
//                       <td className="p-4 text-gray-700">{pallet.pallet}</td>
//                       <td className="p-4 text-gray-700">{pallet.quantity}</td>
//                       <td className="p-4">
//                         <select
//                           value={pallet.action}
//                           onChange={(e) => handlePalletChange(pallet.id, 'action', e.target.value)}
//                           className="p-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500 bg-white"
//                         >
//                           {['Accept', 'Damaged', 'Not Received', 'Short', 'Different Make'].map((option) => (
//                             <option key={option} value={option}>{option}</option>
//                           ))}
//                         </select>
//                       </td>
//                       <td className="p-4">
//                         <input
//                           type="checkbox"
//                           checked={pallet.flag}
//                           disabled={pallet.action === 'Accept'}
//                           onChange={(e) => handlePalletChange(pallet.id, 'flag', e.target.checked)}
//                           className="h-4 w-4 text-gray-600 rounded focus:ring-gray-500"
//                         />
//                       </td>
//                       <td className="p-4">
//                         <input
//                           type="file"
//                           multiple
//                           onChange={(e) => handleFileUpload(pallet.id, e.target.files)}
//                           className="text-sm text-gray-600"
//                         />
//                       </td>
//                       <td className="p-1">
//                         <textarea
//                           value={pallet.remarks}
//                           onChange={(e) => handlePalletChange(pallet.id, 'remarks', e.target.value)}
//                           className="w-full p-2 h-10 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
//                           placeholder="Enter remarks"
//                         />
//                       </td>
//                       <td className="p-4">
//                         {pallet.isManual && (
//                           <button
//                             type="button"
//                             onClick={() => deletePallet(pallet.id)}
//                             className="text-red-600 hover:text-red-800"
//                           >
//                             <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
//                             </svg>
//                           </button>
//                         )}
//                       </td>
//                     </tr>
//                   ))}
//                   <tr className="border-t bg-gray-50">
//                     {['pallet', 'quantity'].map((field) => (
//                       <td key={field} className="p-4">
//                         <input
//                           type={field === 'quantity' ? 'number' : 'text'}
//                           name={field}
//                           value={newPallet[field]}
//                           onChange={handleChange(setNewPallet)}
//                           onBlur={addNewPallet}
//                           className="w-full p-2 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
//                           placeholder={`Enter ${field} ${field === 'quantity' ? '' : 'ID'}`}
//                         />
//                       </td>
//                     ))}
//                     <td colSpan="5" className="p-4"></td>
//                   </tr>
//                 </tbody>
//               </table>
//             </div>
//             <p className="mt-4 text-lg font-semibold text-gray-700">
//               Total Accepted Quantity: <span className="text-green-600">{totalAcceptedQuantity}</span>
//             </p>
//           </div>
//           <div className="mb-8">
//             <h2 className="text-xl font-semibold text-gray-700 mb-4">Receiving & Verification Details</h2>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
//               {['location', 'receivedBy', 'receivedDateTime', 'remarks'].map((field) => (
//                 <div key={field}>
//                   <label className="block text-sm font-medium text-gray-600">{field.replace(/([A-Z])/g, ' $1').trim()}</label>
//                   <input
//                     type={field === 'receivedDateTime' ? 'datetime-local' : 'text'}
//                     name={field}
//                     required
//                     value={receivingDetails[field]}
//                     onChange={handleChange(setReceivingDetails)}
//                     className="mt-1 w-full p-3 border rounded-lg focus:ring-2 focus:ring-gray-500 focus:border-gray-500"
//                     placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
//                   />
//                 </div>
//               ))}
//               <div className="flex items-center">
//                 <input
//                   type="checkbox"
//                   name="checked"
//                   checked={receivingDetails.checked}
//                   onChange={handleChange(setReceivingDetails)}
//                   className="h-4 w-4 rounded accent-green-500"
//                 />
//                 <label className="ml-2 text-sm font-medium text-gray-600">Checked</label>
//               </div>
//             </div>
//           </div>
//           <VehicleInspectionUpload onFilesChange={setUploadedFiles} />
//           <div className="flex justify-end">
//             <button type="submit" className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 shadow-md">
//               Submit
//             </button>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default VehicleInspection;